import React, { useMemo } from 'react'
import { SellerQueueItem } from '../../contexts/SellerQueueContext'
import './index.css'

export interface SellerQueueCardProps {
  queueEntry: SellerQueueItem | null
  isLoading?: boolean
  error?: Error | null
  onEnterQueue?: () => Promise<void>
  onPause?: () => Promise<void>
  onResume?: () => Promise<void>
  onLeaveQueue?: () => Promise<void>
  onStartService?: () => Promise<void>
  onEndService?: () => void
  isServiceActive?: boolean
  isPauseLoading?: boolean
  isResumeLoading?: boolean
  isLeaveLoading?: boolean
  isStartServiceLoading?: boolean
}

/**
 * SellerQueueCard
 * Displays seller's position in queue, status, and action buttons
 * Used in both SellerDashboard and SellerAttendance page
 */
export const SellerQueueCard: React.FC<SellerQueueCardProps> = ({
  queueEntry,
  isLoading,
  error,
  onEnterQueue,
  onPause,
  onResume,
  onLeaveQueue,
  onStartService,
  onEndService,
  isServiceActive,
  isPauseLoading,
  isResumeLoading,
  isLeaveLoading,
  isStartServiceLoading,
}) => {
  // Determine status color and label
  const statusInfo = useMemo(() => {
    if (!queueEntry) {
      return { color: 'neutral', label: 'Offline', class: 'status-offline' }
    }

    switch (queueEntry.status) {
      case 'available':
        return { color: 'success', label: 'Disponível', class: 'status-available' }
      case 'paused':
        return { color: 'warning', label: 'Pausado', class: 'status-paused' }
      case 'in_service':
        return { color: 'info', label: 'Em Atendimento', class: 'status-in-service' }
      case 'offline':
      default:
        return { color: 'neutral', label: 'Offline', class: 'status-offline' }
    }
  }, [queueEntry])

  // Determine which buttons to show based on status
  const showEnterButton = !queueEntry
  const showPauseButton = queueEntry?.status === 'available'
  const showResumeButton = queueEntry?.status === 'paused'
  const showLeaveButton = queueEntry?.status === 'available' || queueEntry?.status === 'paused'
  const showStartServiceButton = queueEntry?.status === 'available' && !isServiceActive
  const showEndServiceButton = queueEntry?.status === 'in_service' || isServiceActive

  const handleEnterQueue = async () => {
    try {
      await onEnterQueue?.()
    } catch (err) {
      console.error('Erro ao entrar na fila:', err)
    }
  }

  const handlePause = async () => {
    try {
      await onPause?.()
    } catch (err) {
      console.error('Erro ao pausar:', err)
    }
  }

  const handleResume = async () => {
    try {
      await onResume?.()
    } catch (err) {
      console.error('Erro ao retomar:', err)
    }
  }

  const handleLeaveQueue = async () => {
    try {
      await onLeaveQueue?.()
    } catch (err) {
      console.error('Erro ao sair da fila:', err)
    }
  }

  const handleStartService = async () => {
    try {
      await onStartService?.()
    } catch (err) {
      console.error('Erro ao iniciar atendimento:', err)
    }
  }

  return (
    <div className="seller-queue-card">
      {/* Header */}
      <div className="card-header">
        <h3>Status da Fila</h3>
        <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>
      </div>

      {/* Error State */}
      {error && (
        <div className="card-error">
          <p>⚠️ Erro: {error.message}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !queueEntry && (
        <div className="card-loading">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      )}

      {/* No Queue Entry */}
      {!queueEntry && !isLoading && (
        <div className="card-empty">
          <p>Você não está na fila</p>
        </div>
      )}

      {/* Queue Info */}
      {queueEntry && (
        <div className="card-content">
          <div className="position-display">
            <div className="position-number">#{queueEntry.position}</div>
            <div className="position-label">Posição na fila</div>
          </div>

          <div className="queue-details">
            <div className="detail-row">
              <span className="detail-label">Status do Vendedor:</span>
              <span className={`detail-value ${statusInfo.class}`}>{statusInfo.label}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Última Atualização:</span>
              <span className="detail-value">
                {queueEntry.updated ? new Date(queueEntry.updated).toLocaleTimeString('pt-BR') : '—'}
              </span>
            </div>
            {queueEntry.lastCalledAt && (
              <div className="detail-row">
                <span className="detail-label">Chamado em:</span>
                <span className="detail-value">
                  {new Date(queueEntry.lastCalledAt).toLocaleTimeString('pt-BR')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="card-actions">
        {/* Enter Queue Button */}
        {showEnterButton && (
          <button
            className="btn btn-primary btn-full"
            onClick={handleEnterQueue}
            disabled={isLoading}
            title="Entrar na fila de atendimento"
          >
            ➕ Entrar na Fila
          </button>
        )}

        {/* Service Buttons Row */}
        {(showPauseButton || showResumeButton || showLeaveButton) && (
          <div className="button-row">
            {showPauseButton && (
              <button
                className="btn btn-warning"
                onClick={handlePause}
                disabled={isPauseLoading}
                title="Pausar atendimento temporariamente"
              >
                ⏸ Pausar
              </button>
            )}

            {showResumeButton && (
              <button
                className="btn btn-success"
                onClick={handleResume}
                disabled={isResumeLoading}
                title="Retomar atendimento"
              >
                ▶ Retomar
              </button>
            )}

            {showLeaveButton && (
              <button
                className="btn btn-danger"
                onClick={handleLeaveQueue}
                disabled={isLeaveLoading}
                title="Sair da fila de atendimento"
              >
                ✕ Sair
              </button>
            )}
          </div>
        )}

        {/* Start Service Button */}
        {showStartServiceButton && (
          <button
            className="btn btn-success btn-full"
            onClick={handleStartService}
            disabled={isStartServiceLoading}
            title="Iniciar atendimento com cliente"
          >
            🎯 Iniciar Atendimento
          </button>
        )}

        {/* End Service Button */}
        {showEndServiceButton && (
          <button
            className="btn btn-danger btn-full"
            onClick={onEndService}
            title="Finalizar atendimento com cliente"
          >
            🏁 Encerrar Atendimento
          </button>
        )}
      </div>
    </div>
  )
}
